import { useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import type { ClienteAdmin, EscritorioAdmin } from '@/lib/plataforma_admin';
import { EncerramentoControle } from './EncerramentoControle';
import {
  Blueprint,
  Button,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';

interface EscritoriosSectionProps {
  escritorios: EscritorioAdmin[] | null;
  clientes: ClienteAdmin[];
  erro: string | null;
  salvando: boolean;
  // `unknown` de propósito: a página devolve o boolean de sucesso do
  // `executar`, e esta seção só aguarda a conclusão.
  onVincular: (escritorioId: string, bancoId: string | null) => Promise<unknown>;
  onMarcarPlataforma: (escritorioId: string, isPlatform: boolean) => Promise<unknown>;
  onEncerrar: (escritorioId: string, encerrado: boolean) => Promise<unknown>;
}

function _rotuloAmbiente(escritorio: EscritorioAdmin): string {
  if (escritorio.is_platform) return ptBR.admin.ambientePlataforma;
  if (escritorio.total_bancos > 0) return ptBR.admin.ambienteCliente;
  return ptBR.admin.ambienteEscritorio;
}

/**
 * O que a coluna de vínculo diz, honrando o N:N (E65.F2.T02). // ux-neutrality:allow doc-comment
 *
 * O servidor só nomeia o cliente quando há EXATAMENTE um: com dois, `banco_nome` // ux-neutrality:allow doc-comment
 * vem nulo porque nenhum cabe na célula. Ler só `banco_nome` fazia a tela // ux-neutrality:allow doc-comment
 * anunciar "sem vínculo" para o escritório que atende dois clientes — o vínculo // ux-neutrality:allow doc-comment
 * novo entrava e a tela negava que tivesse entrado. Quem responde "quantos" é // ux-neutrality:allow doc-comment
 * `total_bancos`. // ux-neutrality:allow doc-comment
 */
function _rotuloVinculo(escritorio: EscritorioAdmin): string {
  if (escritorio.banco_nome !== null) return escritorio.banco_nome;
  if (escritorio.total_bancos > 1) {
    return ptBR.admin.vinculoMultiplo.replace('{total}', String(escritorio.total_bancos));
  }
  return ptBR.admin.semVinculo;
}

/**
 * Escritórios de todos os ambientes, com vínculo e ações (E53.F5). // ux-neutrality:allow doc-comment
 *
 * A coluna de casos mostra o marcador de dado ausente quando o servidor devolve // ux-neutrality:allow doc-comment
 * `null` — o que significa "fora do alcance de leitura", e não "nenhum caso". // ux-neutrality:allow doc-comment
 */
export function EscritoriosSection({
  escritorios,
  clientes,
  erro,
  salvando,
  onVincular,
  onMarcarPlataforma,
  onEncerrar,
}: EscritoriosSectionProps) {
  const [selecionado, setSelecionado] = useState<Record<string, string>>({});

  function vinculoAtual(escritorio: EscritorioAdmin): string {
    return selecionado[escritorio.id] ?? escritorio.banco_id ?? '';
  }

  return (
    <section className="flex flex-col gap-3" data-testid="admin-escritorios">
      <h2 className="text-heading-sm text-fg">{ptBR.admin.escritoriosSectionTitle}</h2>

      {erro !== null ? (
        <p data-testid="escritorios-erro" className="text-body-md text-danger-700">
          {erro}
        </p>
      ) : escritorios === null ? (
        <p data-testid="escritorios-carregando" className="text-body-md text-fg-muted">
          {ptBR.admin.escritoriosCarregando}
        </p>
      ) : escritorios.length === 0 ? (
        <p data-testid="escritorios-vazio" className="text-body-md text-fg-muted">
          {ptBR.admin.escritoriosVazio}
        </p>
      ) : (
        <Blueprint className="min-w-0">
          <div className="min-w-0 overflow-x-auto [contain:layout]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{ptBR.admin.escritorioColNome}</TableHead>
                  <TableHead>{ptBR.admin.escritorioColAmbiente}</TableHead>
                  <TableHead>{ptBR.admin.escritorioColCliente}</TableHead>
                  <TableHead>{ptBR.admin.escritorioColPessoas}</TableHead>
                  <TableHead>{ptBR.admin.escritorioColCasos}</TableHead>
                  <TableHead>{ptBR.admin.escritorioColAcoes}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {escritorios.map((escritorio) => (
                  <TableRow
                    key={escritorio.id}
                    className="align-top"
                    data-testid="escritorio-linha"
                  >
                    <TableCell className="text-fg" data-testid="escritorio-nome">
                      {escritorio.nome}
                      {escritorio.ativo ? null : (
                        <span
                          className="ml-2 border border-border px-1.5 text-[11px] uppercase tracking-[0.08em] text-fg-dim"
                          data-testid="escritorio-encerrado"
                        >
                          {ptBR.admin.escritorioEncerradoMarca}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-fg-muted">{_rotuloAmbiente(escritorio)}</TableCell>
                    <TableCell className="text-fg-muted" data-testid="escritorio-cliente">
                      {_rotuloVinculo(escritorio)}
                    </TableCell>
                    <TableCell className="text-fg-muted" data-testid="escritorio-pessoas">
                      {escritorio.total_usuarios}
                    </TableCell>
                    <TableCell className="text-fg-muted" data-testid="escritorio-casos">
                      {escritorio.total_casos ?? ptBR.admin.valorAusente}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        {escritorio.is_platform ? null : (
                          <>
                            <label className="sr-only" htmlFor={`vinculo-${escritorio.id}`}>
                              {ptBR.admin.vincularLabel}
                            </label>
                            <Select
                              id={`vinculo-${escritorio.id}`}
                              value={vinculoAtual(escritorio)}
                              disabled={salvando}
                              onChange={(evento) =>
                                setSelecionado((atual) => ({
                                  ...atual,
                                  [escritorio.id]: evento.target.value,
                                }))
                              }
                              data-testid={`vinculo-select-${escritorio.id}`}
                            >
                              <option value="">{ptBR.admin.desvincularOpcao}</option>
                              {clientes.map((cliente) => (
                                <option key={cliente.id} value={cliente.id}>
                                  {cliente.nome}
                                </option>
                              ))}
                            </Select>
                            <Button
                              variant="secondary"
                              size="sm"
                              type="button"
                              disabled={salvando}
                              onClick={() => {
                                const escolhido = vinculoAtual(escritorio);
                                void onVincular(escritorio.id, escolhido === '' ? null : escolhido);
                              }}
                              data-testid={`vinculo-salvar-${escritorio.id}`}
                            >
                              {ptBR.admin.vincularSubmit}
                            </Button>
                          </>
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          disabled={salvando}
                          onClick={() =>
                            void onMarcarPlataforma(escritorio.id, !escritorio.is_platform)
                          }
                          data-testid={`plataforma-${escritorio.id}`}
                        >
                          {escritorio.is_platform
                            ? ptBR.admin.desmarcarPlataforma
                            : ptBR.admin.marcarPlataforma}
                        </Button>
                        <EncerramentoControle
                          escritorio={escritorio}
                          salvando={salvando}
                          onDefinir={onEncerrar}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Blueprint>
      )}
    </section>
  );
}
